<template>
    <div class="dot-plot-container">
        <svg
            :width="width"
            :height="height"
            overflow="visible"
        >
            <defs>
                <clipPath :id="clipId">
                    <rect
                        :width="width"
                        :height="height"
                        :rx="height / 2"
                        :ry="height"
                    />
                    <!-- <circle
                        :cx="height / 2"
                        :cy="height / 2"
                        :r="height / 2"
                    />
                    <circle
                        :cx="width - height / 2"
                        :cy="height / 2"
                        :r="height / 2"
                    /> -->
                </clipPath>
            </defs>
            <rect
                class="background"
                :clip-path="`url(#${clipId})`"
                :width="width"
                :height="height"
            />

            <circle
                v-if="value != null"
                class="value"
                :cx="x"
                :cy="height / 2"
                :r="height / 2"
                @mouseover="onMouseEnter({value, compareValue, type: 'value'})"
                @mouseout="onMouseLeave"
            />

            <g v-if="compareValue !== undefined && compareValue !== null">
                <line
                    v-if="compareValue !== -1"
                    :class="`compare-value ${tooltipContent && tooltipContent.type === 'compare' ? 'hover' : ''}`"
                    :x1="compareX"
                    :x2="compareX"
                    :y1="-height * 0.1"
                    :y2="height * 1.1"
                />
                <rect
                    class="hover-area"
                    :x="compareX - 10"
                    :y="-height * 0.1"
                    :width="20"
                    :height="height * 1.2"
                    @mouseover="onMouseEnter({value, compareValue, type: 'compare'})"
                    @mouseout="onMouseLeave"
                />
            </g>

            <text
                ref="compare-label"
                class="compare-label"
                :x="compareX"
                :y="height + 10"
            >
                <slot
                    name="label-content"
                    :value="compareValue"
                />
            </text>
        </svg>

        <div
            v-if="tooltipContent"
            ref="tooltip"
            class="tooltip-content"
            :style="tooltipPos"
        >
            <slot
                name="tooltip-content"
                :value="tooltipContent.value"
                :compare-value="tooltipContent.compareValue"
                :type="tooltipContent.type"
            />
        </div>
    </div>
</template>

<script>
import { setTransformToPreventOverflow } from './helpers';

const { scaleLinear } = d3;

export default {
    props: {
        value: {
            type: Number,
            required: true
        },
        compareValue: {
            type: Number,
            default: null
        },
        domain: {
            type: Array,
            default: () => [0, 1]
        },
        width: {
            type: Number,
            default: 100
        },
        height: {
            type: Number,
            default: 10
        }
    },
    data() {
        return {
            clipId: `dot-plot-clip-${Math.floor(Math.random() * 1000000)}`,
            tooltipContent: null
        };
    },
    computed: {
        scale() {
            return scaleLinear()
                .domain(this.domain)
                .range([this.height / 2, this.width - this.height / 2])
                .clamp(true);
        },
        x() {
            return this.scale(this.value);
        },
        compareX() {
            if (this.compareValue === undefined || this.compareValue === null) {
                return 0;
            }
            return this.scale(this.compareValue);
        },
        hoverAreaX() {
            return Math.min(this.scale(this.value) - this.height / 2, this.scale(this.compareValue)) - 5;
        },
        tooltipPos() {
            const left = Math.max(
                this.scale(this.value) + this.height / 2,
                this.scale(this.compareValue)
            ) + 5;
            return { left: `${left}px` };
        }
    },
    mounted() {
        this.fixLabelPosition();
    },
    async updated() {
        await this.$nextTick();
        this.fixLabelPosition();
        if (this.tooltipContent) {
            this.fixTooltipPosition();
        }
    },
    methods: {
        onMouseEnter(content) {
            if (this.$scopedSlots['tooltip-content']) {
                this.tooltipContent = content;
            }
        },
        onMouseLeave() {
            this.tooltipContent = null;
        },
        fixLabelPosition() {
            setTransformToPreventOverflow(this.$refs['compare-label']);
        },
        fixTooltipPosition() {
            const rect = this.$refs.tooltip.getBoundingClientRect();
            if (rect.right > window.innerWidth) {
                const xAdjust = (rect.right - window.innerWidth) + 25;
                this.$refs.tooltip.style.transform = `translate(-${xAdjust}px, 10px)`;
            }
        }
    }
};
</script>

<style src="./common.chart.css" scoped></style>
<style lang="postcss" scoped>
div.dot-plot-container {
    @apply relative;
}
svg {
    overflow: visible !important;
}

rect, circle {
    @apply fill-current;
}
circle.value {
    @apply cursor-pointer;
}
circle.value:hover {
    @apply opacity-75;
}
rect.hover-area {
    @apply text-transparent cursor-pointer;
}
.background {
    @apply text-neutral-4;
}

line.compare-value {
    @apply stroke-current text-neutral-2;
    stroke-width: 4px;
}
g:hover line.compare-value {
    @apply text-neutral-label;
    stroke-width: 5px;
}

text.compare-label {
    @apply text-neutral-text fill-current;
}
</style>
